import React, { useEffect, useRef, useState } from "react";
import { uploadToR2 } from "../lib/api.js";

// In-browser recording (MediaRecorder) → R2, with a live duration + waveform.
// Text is always the default elsewhere; this is the optional audio path.
export default function VoiceRecorder({ token, quest_key, question_key, peer, onUploaded, t }) {
  const tr = t || ((k) => ({
    recordInstead: "Record audio instead", recording: "Recording…", stopSave: "Stop & save",
    uploading: "Uploading…", audioSaved: "Audio saved.", reRecord: "Re-record",
    micError: "Microphone not available. You can type your answer instead.",
    uploadError: "Upload failed. Try again, or type your answer.",
  }[k] || k));
  const [state, setState] = useState("idle"); // idle | recording | uploading | done
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const canvasRef = useRef(null);

  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  function stopVisuals() {
    cancelAnimationFrame(rafRef.current);
    clearInterval(timerRef.current);
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} audioCtxRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((x) => x.stop()); streamRef.current = null; }
  }
  useEffect(() => () => stopVisuals(), []);

  function draw() {
    const canvas = canvasRef.current, analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;
    const bins = analyser.frequencyBinCount;
    const data = new Uint8Array(bins);
    analyser.getByteFrequencyData(data);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "#2b50e2";
    const bw = width / bins;
    for (let i = 0; i < bins; i++) {
      const v = data[i] / 255;
      const h = Math.max(2, v * height);
      ctx.fillRect(i * bw + bw * 0.25, (height - h) / 2, Math.max(1, bw * 0.5), h);
    }
    rafRef.current = requestAnimationFrame(draw);
  }

  async function start() {
    setError(null); setElapsed(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.start();
      mediaRef.current = mr;
      setState("recording");

      // Live waveform + timer.
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 64;
        src.connect(analyser);
        audioCtxRef.current = ctx; analyserRef.current = analyser;
        rafRef.current = requestAnimationFrame(draw);
      } catch {}
      const started = Date.now();
      timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 500);
    } catch {
      setError(tr("micError"));
    }
  }

  async function stop() {
    const mr = mediaRef.current;
    if (!mr) return;
    await new Promise((r) => { mr.addEventListener("stop", r, { once: true }); mr.stop(); });
    stopVisuals();
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
    setPreviewUrl(URL.createObjectURL(blob));
    setState("uploading");
    try {
      const { key, mime } = await uploadToR2({
        token, quest_key, question_key, kind: "audio", blob, peer,
        filename: `recording.${(blob.type.split("/")[1] || "webm").split(";")[0]}`,
      });
      await onUploaded({ key, mime });
      setState("done");
    } catch {
      setError(tr("uploadError"));
      setState("idle");
    }
  }

  return (
    <div>
      <div className="recorder">
        {state === "idle" && (
          <button type="button" className="subtle" onClick={start}>
            <span className="subtle__dot" aria-hidden /> {tr("recordInstead")}
          </button>
        )}
        {state === "recording" && (
          <div className="rec-live">
            <span className="rec-dot" aria-hidden />
            <span className="rec-time qf-num">{mmss(elapsed)}</span>
            <canvas ref={canvasRef} className="rec-wave" width="240" height="28" aria-hidden />
            <button type="button" className="qf-btn qf-btn--primary" onClick={stop}>{tr("stopSave")}</button>
          </div>
        )}
        {state === "uploading" && <span className="muted-sm">{tr("uploading")}</span>}
        {state === "done" && <span className="saved"><b>✓</b> {tr("audioSaved")}</span>}
        {state === "done" && (
          <button type="button" className="subtle" onClick={start}>{tr("reRecord")}</button>
        )}
      </div>
      {previewUrl && <audio controls src={previewUrl} />}
      {error && <p className="muted-sm" style={{ color: "#b23" }}>{error}</p>}
    </div>
  );
}
